import { instance } from "@/apis";
import { useMutation, useQueryClient, InfiniteData } from "@tanstack/react-query";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import CustomToast from "@/components/shared/custom-toast";
import { useAiAgentStore } from "@/stores/ai-agent";
import { useRef } from "react";
import { AI_CHAT_PATHNAME, AI_HOME_PATHNAME } from "@/constants/pathname";
import type { KbContentData } from "@/utils/knowledge-content";
import type { ChatItem } from "@/hooks/services/queries/ai-agent/use-chats-query";
import type { ChatListResponse } from "@/hooks/services/queries/ai-agent/use-chats-query";
import { migrateTempChat, updateAssistantMessage, appendOptimisticMessages, mergeServerMessages, type CachedMessage, type UploadedFile } from "./helpers/chat-cache";

// 서버 메시지 타입 (MessageList에서 사용)
export interface ServerMessage {
  chatId: string;
  conversationTime: string;
  message: string;
  messageType: "USER" | "ASSISTANT";
  tool: string;
  referencesContent?: Reference[];
  knowledgeContent?: KbContentData;
  chatType?: "DeepResearch";
  isGenerateFinalReport?: boolean;
}

export interface Source {
  web_search_enabled?: boolean;
  private_kb_enabled?: boolean;
  biomcp_enabled?: boolean;
  paper_search_preferred?: boolean;
}

export interface ChatStreamRequest {
  chatId?: string;
  deepResearch?: boolean;
  selectedJob?: number;
  message: string; // 단일 메시지만 전송 (히스토리는 서버에서 관리)
  source?: Source;
  retry?: string;
  files?: File[]; // multipart/form-data를 위한 파일 배열
}

// SSE 이벤트 타입
export interface ProgressEventData {
  type: "status" | "phase" | "task" | "tool" | "log";
  // status 타입
  status?: "started" | "running" | "completed" | string;
  message?: string;
  // phase 타입
  title?: string;
  summary?: string;
  // task 타입
  taskId?: string | null;
  topic?: string;
  assignedTools?: string[];
  resultSummary?: string;
  // tool 타입
  toolCallId?: string;
  toolName?: string;
  output?: string;
  // log 타입
  source?: string;
  iteration?: number;
}

export interface TokenEventData {
  token: string;
}

export interface FinalReportEventData {
  content?: string;  // legacy
  report?: string;
  references?: Reference[];
}

export interface Reference {
  reference_number: number;
  title: string;
  url: string;
}

export interface TokensEventData {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

export interface StoppedEventData {
  message: string;
  reason: string;
}

export interface ErrorEventData {
  message: string;
  code?: string;
}

export interface BackendEventData {
  chatId: string;
}

// SSE 스트림 이벤트 통합 타입
export interface MetadataEventData {
  format?: string;
  [key: string]: unknown;
}

export interface StreamEvent {
  type:
  | "backend"
  | "progress"
  | "token"
  | "final_report"
  | "references"
  | "tokens"
  | "metadata"
  | "complete"
  | "stopped"
  | "error";
  data?:
  | BackendEventData
  | ProgressEventData
  | ProgressEventData[] // 배열 형태도 지원
  | TokenEventData
  | FinalReportEventData
  | Reference[]
  | TokensEventData
  | StoppedEventData
  | ErrorEventData;
}

/**
 * SSE error 이벤트에서 throw된 에러임을 표시하는 커스텀 에러 클래스
 * catch 블록에서 일반 에러(중단, 네트워크 등)와 구분하여 빈 ASSISTANT만 제거
 */
class StreamEventError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "StreamEventError";
  }
}

/**
 * Instance 인터셉터를 활용한 fetch 요청 헬퍼
 */
async function fetchWithInstanceConfig(
  url: string,
  options: RequestInit
): Promise<Response> {
  const baseURL = instance.defaults.baseURL || "";
  const accessToken = localStorage.getItem("accessToken") || "";

  // FormData를 사용하는 경우 Content-Type을 제거하여 브라우저가 자동으로 boundary 설정하도록 함
  const headers = new Headers(options.headers);
  if (options.body instanceof FormData) {
    headers.delete("Content-Type");
  }

  // 첫 번째 시도
  let response = await fetch(`${baseURL}${url}`, {
    ...options,
    headers: {
      ...Object.fromEntries(headers.entries()),
      Authorization: accessToken,
    },
  });

  // 401 에러 시 토큰 갱신 후 재시도 (instance 인터셉터 로직과 동일)
  if (response.status === 401) {
    try {
      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        window.location.href = "/login";
        throw new Error("No refresh token available");
      }

      // 토큰 갱신 요청
      const refreshResponse = await axios
        .create({
          baseURL: instance.defaults.baseURL,
          headers: {
            "Content-Type": "application/json",
            AuthorizationR: refreshToken,
          },
        })
        .get("/api/user/refresh-token");

      // 토큰 갱신 성공
      if (refreshResponse.data.code?.startsWith("S")) {
        const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
          refreshResponse.data.data;
        localStorage.setItem("accessToken", newAccessToken);
        localStorage.setItem("refreshToken", newRefreshToken);

        // 새 토큰으로 재시도
        const retryHeaders = new Headers(options.headers);
        if (options.body instanceof FormData) {
          retryHeaders.delete("Content-Type");
        }
        response = await fetch(`${baseURL}${url}`, {
          ...options,
          headers: {
            ...Object.fromEntries(retryHeaders.entries()),
            Authorization: newAccessToken,
          },
        });
      } else {
        // 토큰 갱신 실패
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        window.location.href = "/login";
        throw new Error("Token refresh failed");
      }
    } catch (error) {
      console.error("Token refresh error:", error);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      window.location.href = "/login";
      throw error;
    }
  }

  return response;
}

/**
 * /api/df/user/chat/isg/stream/demostrate API 호출 및 SSE 스트리밍 처리
 */
export async function* chatStreamGenerator(
  request: ChatStreamRequest
): AsyncGenerator<StreamEvent, void, unknown> {
  // FormData 생성
  const formData = new FormData();

  // body 필드에 JSON 객체를 문자열로 변환하여 추가 (스웨거 형식에 맞춤)
  const bodyData: Record<string, unknown> = {
    message: request.message,
    deepResearch: request.deepResearch ?? true,
    source: request.source ?? {
      web_search_enabled: true,
      private_kb_enabled: true,
      biomcp_enabled: false,
    },
    sdfJobId: request?.selectedJob ?? undefined,
  };

  // chatId가 있으면 추가
  if (request.chatId) {
    bodyData.chatId = request.chatId;
  }

  // retry가 있으면 추가
  if (request.retry) {
    bodyData.retry = request.retry;
  }

  // body 필드에 JSON 문자열로 추가
  formData.append("body", JSON.stringify(bodyData));

  // 파일 첨부 (있는 경우)
  if (request.files && request.files.length > 0) {
    request.files.forEach((file) => {
      formData.append("files", file);
    });
  } else {
    // 파일이 없어도 빈 files 필드를 보내야 할 수도 있음 (스웨거 예시 참고)
    // 하지만 일반적으로는 파일이 있을 때만 보내는 것이 맞음
  }

  // instance 설정을 활용한 fetch 요청 (인터셉터 로직 포함)
  // multipart/form-data는 Content-Type을 자동으로 설정해야 하므로 헤더에서 제거
  const response = await fetchWithInstanceConfig(
    "api/df/user/chat/isg/stream/demonstrate",
    {
      method: "POST",
      headers: {
        Accept: "text/event-stream",
        // Content-Type은 FormData가 자동으로 설정하므로 명시하지 않음
      },
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const body = response.body;
  if (!body) {
    throw new Error("Response body is null");
  }

  const reader = body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();

      if (done) {
        if (buffer.trim().length) {
          yield* parseSSEBuffer(buffer);
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() || "";

      for (const part of parts) {
        yield* parseSSEBuffer(part);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * SSE 버퍼 파싱
 */
function* parseSSEBuffer(chunk: string): Generator<StreamEvent> {
  const lines = chunk.split("\n");
  let eventType = "";
  let dataPayload = "";

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed.startsWith("event:")) {
      eventType = trimmed.slice(6).trim();
    } else if (trimmed.startsWith("data:")) {
      dataPayload += trimmed.slice(5).trim();
    }
  }

  if (!eventType || !dataPayload) return;

  try {
    const parsed = JSON.parse(dataPayload);

    yield {
      type: eventType as StreamEvent["type"],
      data: parsed,
    };
  } catch (error) {
    console.error("Failed to parse SSE data:", error, dataPayload);
  }
}

// Mutation 파라미터
export interface SendChatStreamParams {
  chatId?: string;
  newMessage: string;
  deepResearch?: boolean;
  source?: Source;
  retry?: string;
  files?: File[]; // multipart/form-data를 위한 파일 배열
  selectedJob?: number;
  sdfJobName?: string;
}

// Mutation Context
interface MutationContext {
  previousMessages: ServerMessage[];
  previousChats?: InfiniteData<ChatListResponse>;
  chatId: string;
  isTempChatId: boolean;
}

/**
 * AI 채팅 스트림 Mutation Hook
 * 
 * SSE(Server-Sent Events)를 통해 실시간으로 AI 응답을 스트리밍받아 처리하는 훅입니다.
 * 
 * @param chatId - 기존 채팅방 ID (선택사항). 없으면 임시 채팅방("temp")을 생성합니다.
 * @returns React Query mutation 객체 (mutate 함수 포함)
 * 
 */
export const useDemoChatStreamMutation = (chatId?: string) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // 네비게이션 중복 방지를 위한 ref
  const hasNavigatedRef = useRef(false);

  // AI Agent Store에서 상태 관리 함수들 가져오기
  const setEventType = useAiAgentStore((state) => state.setEventType);
  const addProgress = useAiAgentStore((state) => state.addProgress);
  const clearProgressList = useAiAgentStore((state) => state.clearProgressList);
  const migrateProgressList = useAiAgentStore((state) => state.migrateProgressList);
  const migratePipelineExpandState = useAiAgentStore((state) => state.migratePipelineExpandState);
  const migrateChatSourceSettings = useAiAgentStore((state) => state.migrateChatSourceSettings);
  const clearPipelineExpandState = useAiAgentStore((state) => state.clearPipelineExpandState);
  const addStreamingChatId = useAiAgentStore((state) => state.addStreamingChatId);
  const removeStreamingChatId = useAiAgentStore((state) => state.removeStreamingChatId);
  const setStreamComplete = useAiAgentStore((state) => state.setStreamComplete);
  const clearStreamComplete = useAiAgentStore((state) => state.clearStreamComplete);
  const setShowCreditError = useAiAgentStore((state) => state.setShowCreditError);
  const setShowFileUploadError = useAiAgentStore((state) => state.setShowFileUploadError);


  return useMutation<void, Error, SendChatStreamParams, MutationContext>({
    mutationKey: ["chatStream", chatId ?? "unknown"],

    /**
     * 실제 스트리밍 요청 및 이벤트 처리 로직
     * 
     * 1. Optimistic 업데이트로 사용자 메시지와 빈 Assistant 메시지를 즉시 추가
     * 2. SSE 스트림을 통해 실시간으로 이벤트를 받아 처리
     * 3. 각 이벤트 타입에 따라 적절한 상태 업데이트 수행
     */
    mutationFn: async ({
      chatId: paramSessionId,
      newMessage,
      deepResearch,
      source,
      retry,
      selectedJob,
      sdfJobName,
      files: paramFiles,
    }) => {
      // 채팅 ID 결정: 파라미터 > 훅 파라미터 > 임시 ID 순서로 우선순위 결정
      let sid = paramSessionId ?? chatId ?? "temp";

      /**
       * 서버 메시지로 캐시 동기화
       * - count 일치: 인덱스 기반 merge (캐시 필드 보존)
       * - count 불일치: 서버 데이터로 replace (인덱스 불일치 방지)
       */
      const syncMessagesFromServer = (chatId: string) =>
        instance.get(`/api/df/user/chat/${chatId}`).then((res) => {
          const current = queryClient.getQueryData<CachedMessage[]>(["messages", chatId]) ?? [];
          const next = mergeServerMessages(current, res?.data?.data);
          if (next) queryClient.setQueryData(["messages", chatId], next);
        }).catch(() => {});

      try {
        // 초기화: 네비게이션 플래그 리셋 및 스트리밍 상태 설정
        hasNavigatedRef.current = false;
        clearProgressList(sid);
        clearStreamComplete(sid);

        // 스트리밍 중인 채팅방으로 등록
        addStreamingChatId(sid);

        // 스트리밍 중 누적되는 데이터들
        let accumulatedContent = ""; // 토큰들을 누적하여 완성된 메시지 생성
        let references: Reference[] = []; // 참조 링크 목록
        let tokensUsed: TokensEventData | null = null; // 사용된 토큰 정보
        let receivedCompleteEvent = false; // complete 이벤트 정상 수신 여부 (post-loop cleanup 방지용)
        let isScientificReport = false; // metadata에서 format === "scientific_report"인 경우

        // 스트림 요청 시에만 초기 값을 사용하고, 이벤트 처리 시에는 항상 현재 sid 기준으로 다시 판별한다.
        const isTempChatId = sid.startsWith("temp");
        // 최초 sid 저장 (backend 이벤트로 sid가 실제 chatId로 교체되더라도 원본 tempId 참조 유지)
        // → finally에서 tempId를 streamingChatIds에서 제거하기 위해 필요
        const initialSid = sid;

        // Optimistic 업데이트: 사용자 메시지와 빈 Assistant 메시지를 즉시 추가
        const optimisticUploadedFiles: UploadedFile[] | undefined =
          paramFiles && paramFiles.length > 0
            ? paramFiles.map((file, index) => ({
                fileId: -(index + 1), // optimistic ID (음수로 서버 ID와 구분)
                filename: file.name,
                documentId: "",
                mimeType: file.type,
                createdAt: new Date().toISOString(),
              }))
            : undefined;

        appendOptimisticMessages(queryClient, sid, newMessage, {
          ...(deepResearch ? { chatType: "DeepResearch" as const } : {}),
          ...(optimisticUploadedFiles ? { uploadedFiles: optimisticUploadedFiles } : {}),
          ...(sdfJobName ? { sdfJobName } : {}),
        });

        // SSE 스트림 이벤트 처리 루프
        // 임시 채팅방인 경우 chatId를 서버에 전송하지 않음 (서버가 새 ID 생성)
        for await (const event of chatStreamGenerator({
          chatId: isTempChatId ? undefined : sid,
          message: newMessage,
          deepResearch,
          source,
          retry,
          selectedJob,
          files: paramFiles,
        })) {
          switch (event.type) {
            /**
             * backend: 서버에서 실제 chatId를 받았을 때
             * - 임시 채팅방을 실제 채팅방으로 마이그레이션
             * - 현재 페이지가 임시 채팅방 페이지인 경우 실제 chatId로 navigate
             */
            case "backend":
              if (event.data && "chatId" in event.data) {
                const backendData = event.data as BackendEventData;
                const newChatId = backendData.chatId;

                // 현재 sid가 여전히 temp-로 시작하는지 재확인 (두 번째 backend 이벤트 방어)
                const isCurrentTempSid = sid.startsWith("temp");

                // 초기 호출이 temp 기반이었고, 현재 sid도 여전히 temp-인 경우에만 마이그레이션 수행
                if (isTempChatId && isCurrentTempSid) {
                  addStreamingChatId(newChatId);
                  migrateTempChat(queryClient, sid, newChatId);
                  migrateProgressList(sid, newChatId);
                  migratePipelineExpandState(sid, newChatId);
                  migrateChatSourceSettings(sid, newChatId);

                  if (!hasNavigatedRef.current) {
                    const currentPath = window.location.pathname;
                    const isHome = currentPath === AI_HOME_PATHNAME;
                    const isTempChatPage = currentPath.endsWith(`/${sid}`);

                    if (isHome || isTempChatPage) {
                      hasNavigatedRef.current = true;
                      navigate(`${AI_CHAT_PATHNAME}/${newChatId}`, { replace: true });
                    }
                  }
                }

                setTimeout(() => {
                  instance.get<{ data: ChatListResponse }>("/api/df/user/chat/list")
                    .then((res) => {
                      const firstPageChats = res.data?.data?.content ?? [];
                      const updated = firstPageChats.find((c) => c.chatId === newChatId);
                      if (!updated?.chatTitle || updated.chatTitle === "New Chat") return;
 
                      queryClient.setQueryData<InfiniteData<ChatListResponse>>(["chats"], (old) => {
                        if (!old?.pages) return old;
                        return {
                          ...old,
                          pages: old.pages.map((page) => ({
                            ...page,
                            content: page.content.map((chat) =>
                              chat.chatId === newChatId
                                ? { ...chat, chatTitle: updated.chatTitle }
                                : chat
                            ),
                          })),
                        };
                      });
                    })
                    .catch(() => {});
                }, 2000);
 

                // 이제부터 새 chatId 사용
                sid = newChatId;
              }
              break;

            /**
             * progress: 작업 진행 상황 업데이트
             * - Deep Research 진행 단계, 상태, 태스크 정보 등을 표시
             * - 배열 또는 단일 객체 형태로 전달될 수 있음
             */
            case "progress":
              setEventType("progress");
              if (event.data) {
                if (Array.isArray(event.data)) {
                  // 배열인 경우 각 항목을 순회하며 추가
                  event.data.forEach((item) => {
                    if (typeof item === "object" && item !== null) {
                      addProgress(sid, item as ProgressEventData);
                    }
                  });
                } else if (typeof event.data === "object") {
                  // 단일 객체인 경우 직접 추가
                  addProgress(sid, event.data as ProgressEventData);
                }
              }
              break;

            /**
             * token: 스트리밍되는 토큰 단위 응답
             * - 각 토큰을 누적하여 실시간으로 Assistant 메시지 업데이트
             * - 사용자에게 타이핑 효과처럼 보이게 함
             */
            case "token":
              if (event.data && "token" in event.data) {
                accumulatedContent += event.data.token;
                updateAssistantMessage(queryClient, sid, accumulatedContent, deepResearch ? { chatType: "DeepResearch" } : undefined);
              }
              break;

            /**
             * final_report: 최종 리포트 완성
             * - 누적된 references와 함께 최종 메시지 업데이트
             * - token 이벤트로 받은 내용을 최종적으로 확정
             */
            case "final_report":
              if (event.data && ("content" in event.data || "report" in event.data)) {
                const finalData = event.data as FinalReportEventData;
                const reportContent = finalData.report ?? finalData.content ?? "";
                // final_report 안에 references가 포함된 경우 우선 사용, 없으면 별도 references 이벤트 값 사용
                const reportRefs = finalData.references ?? references;
                if (reportContent) {
                  updateAssistantMessage(queryClient, sid, reportContent, {
                    referencesContent: reportRefs as { reference_number: number; title: string; url: string; content?: string }[],
                     ...(deepResearch ? { chatType: "DeepResearch" as const } : {}),
                  });
                }
              }
              break;

            /**
             * references: 참조 링크 정보
             * - 응답에 사용된 참조 논문, 문서 등의 링크 목록
             * - final_report 이벤트에서 사용하기 위해 저장
             */
            case "references":
              if (Array.isArray(event.data)) {
                const referenceData = event.data as Reference[];
                references = referenceData;
                // final_report보다 늦게 올 수 있으므로 캐시도 즉시 업데이트
                const currentMsg = queryClient.getQueryData<ServerMessage[]>(["messages", sid]) ?? [];
                const lastMsg = currentMsg[currentMsg.length - 1];
                if (lastMsg?.messageType === "ASSISTANT") {
                  updateAssistantMessage(queryClient, sid, lastMsg.message, {
                    referencesContent: referenceData as { reference_number: number; title: string; url: string; content?: string }[],
                    ...(deepResearch ? { chatType: "DeepResearch" as const } : {}),
                  });
                }
              }
              break;
            /**
             * metadata: 응답 메타데이터
             * - format === "scientific_report"인 경우 complete 시점에 isGenerateFinalReport 세팅
             */
            case "metadata":
              if (event.data && typeof event.data === "object" && "format" in event.data) {
                const metaData = event.data as MetadataEventData;
                if (metaData.format === "scientific_report") {
                  isScientificReport = true;
                }
              }
              break;
              
            /**
             * tokens: 사용된 토큰 통계
             * - prompt_tokens, completion_tokens, total_tokens 정보
             * - 채팅방별 토큰 사용량 추적을 위해 저장
             */
            case "tokens":
              if (event.data && "total_tokens" in event.data) {
                tokensUsed = event.data;
                queryClient.setQueryData(["chatTokens", sid], tokensUsed);
              }
              break;

            /**
             * complete: 스트리밍 완료
             * - 모든 응답이 완료되었음을 표시
             */
            case "complete":
              receivedCompleteEvent = true;
              setEventType("complete");
              setStreamComplete(sid);
              removeStreamingChatId(sid);

              // metadata에서 scientific_report 포맷으로 받은 경우 isGenerateFinalReport 세팅
              if (isScientificReport) {
                updateAssistantMessage(queryClient, sid, accumulatedContent, { isGenerateFinalReport: true });
              }
              
              clearProgressList(sid);
              clearPipelineExpandState(sid);

              queryClient.setQueryData<InfiniteData<ChatListResponse>>(["chats"], (old) => {
                if (!old?.pages) return old;

                const now = new Date().toISOString();
                const firstChat = old.pages[0]?.content[0];
                if (firstChat?.chatId === sid) {
                  return {
                    ...old,
                    pages: old.pages.map((page, idx) =>
                      idx === 0
                        ? { ...page, content: [{ ...firstChat, updatedAt: now }, ...page.content.slice(1)] }
                        : page
                    ),
                  };
                }

                let completedChat: ChatItem | null = null;
                const pagesWithoutCompletedChat = old.pages.map((page) => ({
                  ...page,
                  content: page.content.filter((c) => {
                    if (c.chatId === sid) {
                      completedChat = { ...c, updatedAt: now };
                      return false;
                    }
                    return true;
                  }),
                }));

                if (!completedChat) {
                  console.warn(`[Complete] Chat ${sid} not found in cache. Server sync may be needed.`);
                  return old;
                }

                const updatedPages = pagesWithoutCompletedChat.map((page, index) =>
                  index === 0
                    ? { ...page, content: [completedChat!, ...page.content] }
                    : page
                );

                return {
                  ...old,
                  pages: updatedPages,
                };
              });

              setTimeout(() => syncMessagesFromServer(sid), 500);
              break;

            /**
             * stopped: 사용자에 의한 중단
             * 주의: 백엔드에서 stopped 이벤트를 보내지 않음
             * 중지 시 onError로 처리됨
             */
            case "stopped":
              setEventType("stopped");
              clearProgressList(sid);
              break;

            /**
             * error: 에러 발생
             * - 서버에서 에러 메시지를 받으면 예외를 던져 onError 핸들러로 전달
             */
            case "error":
              setEventType("error");
              if (event.data && "message" in event.data) {
                const errorData = event.data as ErrorEventData;
                const code =
                  errorData.code ?? (event.data as { code?: string }).code ?? "";
                const isFileUploadError =
                  code === "F001" || code === "E413" || code === "F007";
                const isRetryError =
                  code === "E503" || errorData.message?.includes("No documents found");
                const isLongAnswerError = errorData.message?.includes("This answer is long, so only part of it is shown.");
                const isPdfLimitError = errorData.message?.includes("This PDF exceeds the analysis limit.");

                if (isFileUploadError) {
                  setShowFileUploadError(true);
                } else if (isRetryError) {
                  toast.custom(
                    (id) => (
                      <CustomToast
                        type="error"
                        text={"We couldn't complete your request.\nPlease try again in a moment."}
                        onClose={() => toast.dismiss(id)}
                      />
                    ),
                    { position: "bottom-center", duration: 3000 }
                  );
                } else if (isLongAnswerError) {
                  toast.custom(
                    (id) => (
                      <CustomToast
                        type="error"
                        text={"This answer is long, so only part of it is shown.\nTo explore further, you can:\n• Ask for a concise summary\n• Focus on a specific section"}
                        onClose={() => toast.dismiss(id)}
                      />
                    ),
                    { position: "bottom-center", duration: 3000 }
                  );
                } else if (isPdfLimitError) {
                  toast.custom(
                    (id) => (
                      <CustomToast
                        type="error"
                        text={"This PDF exceeds the analysis limit.\nPlease upload a shorter document or split the file."}
                        onClose={() => toast.dismiss(id)}
                      />
                    ),
                    { position: "bottom-center", duration: 3000 }
                  );
                } else {
                  toast.custom(
                    (id) => (
                      <CustomToast
                        type="error"
                        text={errorData.message}
                        onClose={() => toast.dismiss(id)}
                      />
                    ),
                    { position: "bottom-center", duration: 3000 }
                  );
                }

                // StreamEventError로 throw → catch 블록에서 빈 ASSISTANT 메시지 제거
                throw new StreamEventError(errorData.message);
              }
              break;
          }
        }

        // for await 루프가 정상 종료된 경우 (complete 이벤트 없이 스트림이 닫힘)
        // 예: 중지 버튼 → 서버가 graceful close → onError가 호출되지 않는 케이스
        // 빈 ASSISTANT 메시지가 남아있으면 스피너가 계속 도는 문제 방지
        // complete 이벤트를 정상 수신한 경우에는 cleanup을 수행하지 않음
        const finalMessages = queryClient.getQueryData<ServerMessage[]>(["messages", sid]) || [];
        const finalLastMsg = finalMessages[finalMessages.length - 1];
        if (!receivedCompleteEvent && finalLastMsg?.messageType === "ASSISTANT" && !finalLastMsg.message) {
          // USER 메시지는 유지하고 빈 ASSISTANT 메시지만 제거
          // (사용자가 자신이 보낸 메시지를 확인하고 재전송할 수 있어야 함)
          queryClient.setQueryData(["messages", sid], finalMessages.slice(0, -1));
        }

      } catch (err: unknown) {
        console.error("Mutation Error:", err);
        const catchMessages = queryClient.getQueryData<ServerMessage[]>(["messages", sid]) || [];
        const catchLastMsg = catchMessages[catchMessages.length - 1];

        if (err instanceof StreamEventError) {
          if (catchLastMsg?.messageType === "ASSISTANT" && !catchLastMsg.message) {
            queryClient.setQueryData(["messages", sid], catchMessages.slice(0, -1));
          }
        } else if (catchLastMsg?.messageType === "ASSISTANT" && !catchLastMsg.message) {
          queryClient.setQueryData(["messages", sid], catchMessages.slice(0, -1));
        }

        // SSE 에러 시 서버 메시지로 캐시 동기화 (인덱스 불일치 방지)
        // 서버가 에러 메시지를 DB에 저장한 후 조회하기 위함
        if (!sid.startsWith("temp")) {
          setTimeout(() => syncMessagesFromServer(sid), 300);
        }
        
        throw err;
      } finally {
        setStreamComplete(sid);
        removeStreamingChatId(sid);
        // backend 이벤트로 tempId → realId 마이그레이션이 일어난 경우,
        // backend 핸들러에서 tempId를 제거하지 않았으므로 여기서 정리
        if (sid !== initialSid) removeStreamingChatId(initialSid);
      }
    },

    /**
     * mutationFn 실행 전 Optimistic Update 수행
     * 
     * 1. 진행 중인 쿼리 취소하여 경쟁 상태 방지
     * 2. 이전 메시지 상태 저장 (롤백용)
     * 3. 새 채팅인 경우 사이드바에 임시 채팅방 추가
     * 
     * @returns MutationContext - 롤백에 필요한 이전 상태들
     */
    onMutate: async ({ chatId: paramSessionId, newMessage: _newMessage }) => {
      // 채팅 ID 결정 (타임스탬프 포함하여 고유성 보장)
      const sid = paramSessionId ?? chatId ?? `temp-${Date.now()}`;
      // 진행 중인 메시지 쿼리 취소 (낙관적 업데이트와 충돌 방지)
      await queryClient.cancelQueries({ queryKey: ["messages", sid] });

      // 이전 메시지 상태 저장 (에러 발생 시 롤백용)
      const previousMessages =
        queryClient.getQueryData<ServerMessage[]>(["messages", sid]) || [];

      // 임시 채팅방을 채팅 목록에 optimistic 추가
      // 크레딧 0원이면 임시 채팅방을 목록에 넣지 않음 (생겼다가 에러로 사라지는 현상 방지)
      let previousChats: InfiniteData<ChatListResponse> | undefined;

      if (sid.startsWith("temp")) {
        // user 캐시: queryFn 반환값({ data: { data: user } }) 또는 select 결과(user)일 수 있음
        const raw = queryClient.getQueryData<{ data?: { data?: { id?: string | number; role?: number; availableCredit?: number } }; id?: string | number; role?: number; availableCredit?: number }>(["user"]);
        const userData = raw && "data" in raw && raw.data?.data ? raw.data.data : raw;
        const role = Number(userData?.role);
        const availableCredit = Number(userData?.availableCredit ?? 0);
        const hasNoCredit = userData != null && role === 1 && availableCredit < 1;

        if (!hasNoCredit) {
          // 채팅 목록 쿼리 데이터 가져오기 (InfiniteQuery 구조)
          previousChats = queryClient.getQueryData<InfiniteData<ChatListResponse>>(["chats"]);

          // 채팅 목록이 이미 로드되어 있는 경우에만 추가
          if (previousChats?.pages?.[0]) {
            const newChat: ChatItem = {
              chatId: sid,
              chatTitle: "New Chat",
              updatedAt: new Date().toISOString(),
              userId: String(userData?.id ?? ""),
            };

            // 첫 번째 페이지 맨 앞에 임시 채팅방 추가
            // 이후 backend 이벤트에서 실제 chatId를 받으면 migrateTempChat으로 교체됨
            queryClient.setQueryData<InfiniteData<ChatListResponse>>(["chats"], {
              ...previousChats,
              pages: previousChats.pages.map((page, index) =>
                index === 0
                  ? { ...page, content: [newChat, ...page.content] }
                  : page
              ),
            });
          }
        }
      }
      return {
        previousMessages,
        previousChats,
        chatId: sid,
        isTempChatId: sid.startsWith("temp"),
      };
    },
    onError: (err, { chatId: paramSessionId }, context) => {
      const sid = paramSessionId ?? chatId ?? context?.chatId;

      // 스트리밍 상태 정리
      setEventType("error");

      if (sid) {
        removeStreamingChatId(sid);
        clearProgressList(sid);
        clearPipelineExpandState(sid);
      }

      // Credit 부족 에러 감지 및 Store 상태 변경 (에러 코드 C004 또는 메시지로 판별)
      const errorMessage = err instanceof Error ? err.message : String(err);
      const errorCode = (err as { code?: string })?.code;
      const isCreditError =
        errorCode === "C004" ||
        errorMessage.includes("잔여 크레딧 부족") ||
        errorMessage.includes("잔여 크레딧 없음") ||
        errorMessage.includes("AiAgentNotEnoughCredit");

      if (isCreditError) {
        setShowCreditError(true);
      }

      // 중단 시 또는 credit 부족 시 마지막 USER 메시지와 빈 ASSISTANT 메시지 제거
      const currentMessages =
        queryClient.getQueryData<ServerMessage[]>(["messages", sid]) || [];

      // 마지막 메시지가 빈 ASSISTANT인지 확인
      const lastMessage = currentMessages[currentMessages.length - 1];
      const isLastEmptyAssistant =
        lastMessage?.messageType === "ASSISTANT" && !lastMessage.message;

      let cleanedMessages = currentMessages;

      if (isLastEmptyAssistant) {
        cleanedMessages = currentMessages.slice(0, -1);
      }

      queryClient.setQueryData(["messages", sid], cleanedMessages);

      // Credit 에러인 경우 임시 채팅방을 목록에서도 제거하고 홈으로 navigate
      if (isCreditError && context?.isTempChatId) {
        const chats = queryClient.getQueryData<InfiniteData<ChatListResponse>>(["chats"]);
        if (chats?.pages) {
          queryClient.setQueryData<InfiniteData<ChatListResponse>>(["chats"], {
            ...chats,
            pages: chats.pages.map((page) => ({
              ...page,
              content: page.content.filter((chat) => chat.chatId !== sid),
            })),
          });
        }

        // 임시 채팅방 URL에 있는 경우 홈으로 돌아가기
        if (window.location.pathname.endsWith(`/${sid}`)) {
          navigate(AI_HOME_PATHNAME, { replace: true });
        }
      }

      // 에러 발생 시 채팅방 삭제하지 않음 (credit 에러 제외)
      // 사용자가 중단하거나 에러가 발생해도 기존 메시지는 보존되어야 함
      // invalidateQueries를 호출하면 다른 스트리밍 중인 채팅방들도 사라질 수 있으므로
      // 서버와 동기화하지 않고 캐시에만 유지
    },
  });
};

/**
 * Chat Stop API 호출
 */
export const stopDemoChatStream = async (chatId: string): Promise<void> => {
  // instance 설정을 활용한 fetch 요청 (인터셉터 로직 포함)
  const response = await fetchWithInstanceConfig(
    "api/df/user/chat/isg/stream/demostrate/stop",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ chatId }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to stop chat: ${response.status}`);
  }

  return response.json();
};
